import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Edit2, MoreHorizontal, RefreshCw, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import React from 'react';
import { ExtendedEntity } from '../types';
import { Plus } from 'lucide-react';

interface EntityHeaderProps {
  entity: ExtendedEntity | undefined;
  onEdit?: () => void;
  onSync?: () => void;
  onDelete?: () => void;
  onAddToStory?: () => void;
  onRefresh?: () => void;
  isSyncing?: boolean;
  isRefreshing?: boolean;
}

/**
 * Component to display the entity sidebar with avatar, stats, and navigation
 */
export const EntityHeader: React.FC<EntityHeaderProps> = ({
  entity,
  onEdit,
  onSync,
  onDelete,
  onAddToStory,
  onRefresh,
  isSyncing,
  isRefreshing
}) => {
  if (!entity) return null;

  return (
    <div className="flex flex-col space-y-6">

      {/* Identity Block */}
      <div className="space-y-4">
        <div className="space-y-1">
          <div className="flex items-start justify-between gap-4">
            <h1 className="text-3xl font-extrabold tracking-tight text-foreground leading-[1.1] break-words">
              {entity.name}
            </h1>

            <div className="flex items-center gap-2">
              {onRefresh && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={onRefresh}
                  disabled={isRefreshing}
                  className="rounded-full bg-background/50 backdrop-blur-sm border-border/60 text-muted-foreground hover:text-foreground h-9 w-9 shadow-sm"
                  title="Refresh data"
                >
                  <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
                </Button>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-full bg-background/50 backdrop-blur-sm border-border/60 text-muted-foreground hover:text-foreground h-9 w-9 shadow-sm"
                    title="Menu"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 p-1.5 rounded-xl border-border/50 shadow-lg bg-card/95 backdrop-blur-sm">
                  {onEdit && (
                    <DropdownMenuItem onClick={onEdit} className="cursor-pointer py-2.5 px-3 rounded-lg focus:bg-primary/10 focus:text-primary transition-colors">
                      <Edit2 className="mr-3 h-4 w-4 opacity-70" />
                      <span className="font-medium text-sm">Edit Details</span>
                    </DropdownMenuItem>
                  )}
                  {onSync && (
                    <DropdownMenuItem onClick={onSync} disabled={isSyncing} className="cursor-pointer py-2.5 px-3 rounded-lg focus:bg-primary/10 focus:text-primary transition-colors">
                      <RefreshCw className={cn("mr-3 h-4 w-4 opacity-70", isSyncing && "animate-spin")} />
                      <span className="font-medium text-sm">{isSyncing ? 'Syncing...' : 'Sync Template'}</span>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="my-1.5 bg-border/40" />
                  {onDelete && (
                    <DropdownMenuItem onClick={onDelete} className="cursor-pointer py-2.5 px-3 rounded-lg focus:bg-destructive/10 text-destructive focus:text-destructive transition-colors">
                      <Trash2 className="mr-3 h-4 w-4" />
                      <span className="font-medium text-sm">Delete {entity.name}</span>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap mt-2">
            <Badge variant="secondary" className="rounded-sm px-1.5 py-0 text-[10px] uppercase font-bold tracking-wider text-muted-foreground/80 bg-muted/50 hover:bg-muted/50 border border-transparent">
              {entity.status}
            </Badge>
            <span className="text-[10px] text-muted-foreground/40 font-light">|</span>
            <span className="text-xs text-muted-foreground font-medium">{entity.templates?.name || 'Unknown Type'}</span>
          </div>
        </div>
      </div>

      {/* Description */}
      {entity.description && (
        <p className="text-sm text-muted-foreground leading-relaxed">
          {entity.description}
        </p>
      )}
    </div>
  );
};
